import { optionalText } from 'aos-helpers/src/helpers/Text'
import { minutesToTaskDuration } from 'aos-helpers/src/helpers/Time'
import { formatFromDateTime, formatToDateTime } from 'aos-helpers/src/helpers/TimeFormat'
import { translateParts } from 'aos-helpers/src/helpers/translations/Translations'
import { UploadedAttachment } from 'aos-services/src/services/attachments/types/UploadedAttachment'
import { AosLocation } from 'aos-services/src/services/common/types/AosLocation'
import {
    severityToneMap,
    translateTaskSeverity,
} from 'aos-services/src/services/common/types/AosSeverity'
import { simpleTaskContractorLabel } from 'aos-services/src/services/tasks/types/task/SimpleTask'
import { Task } from 'aos-services/src/services/tasks/types/task/Task'
import {
    isTaskTemplate,
    type TaskTemplate,
} from 'aos-services/src/services/tasks/types/task/TaskTemplate'
import { translateTaskCategory } from 'aos-services/src/services/tasks/types/TaskCategory'
import {
    taskProcessTypeIcons,
    translateTaskProcessType,
} from 'aos-services/src/services/tasks/types/TaskProcessType'
import { translateTaskBillable } from 'aos-services/src/services/tasks/types/TaskStatus'
import { TaskIcon } from 'aos-tasks'
import { AttachmentReadonlySection } from 'aos-ui/src/components/attachment/AttachmentReadonlySection'
import { Box } from 'aos-ui/src/components/base/Box'
import { ThemeVariant } from 'aos-ui/src/components/base/ThemeVariant'
import {
    LabeledTextElement,
    LabeledTextElementSize,
} from 'aos-ui/src/components/form/labeled/LabeledTextElement'
import { PropsListEntry, type PropsListProps } from 'aos-ui/src/components/list/PropsList'
import { Icon } from 'aos-ui/src/components/svg/Icon'
import {
    ChipSeverity,
    colors,
    DetailsList,
    HStack,
    Link,
    NameTag,
    Text as UniversalText,
} from 'aos-ui-universal'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { DetailedTaskPropertyList } from './detailed-prop-list'

export const BaseTaskPropList = ({
    previewLocation,
    task,
    items: propsItems = [],
    ...rest
}: Partial<PropsListProps> & {
    task: Task | TaskTemplate
    previewLocation?(t: Task | TaskTemplate): void
}) => {
    const { t: translate } = useTranslation()

    const renderEmptyProp = () => {
        return (
            // eslint-disable-next-line react/jsx-no-literals
            <UniversalText size='body' tone='muted'>
                -
            </UniversalText>
        )
    }

    const renderAttachments = (attachments: UploadedAttachment[]) =>
        attachments.length > 0 ? (
            <AttachmentReadonlySection
                attachments={attachments}
                variant={rest.variant}
                size={LabeledTextElementSize.Small}
                maxSize={300}
            />
        ) : (
            <LabeledTextElement
                label={`${translate('attachment.title', { ns: 'common' })}`}
                spacing={8}
                marginTop={16}
                size={LabeledTextElementSize.Small}
                variant={rest.variant}
            >
                {renderEmptyProp()}
            </LabeledTextElement>
        )

    const renderAssignees =
        task.assignees.length > 0 ? (
            <HStack className='flex-wrap gap-2'>
                {task.assignees.map((assignee, index) => (
                    <NameTag key={`${assignee.name}-${index}`}>{assignee.name}</NameTag>
                ))}
            </HStack>
        ) : (
            renderEmptyProp()
        )

    const renderContractors =
        task.contractors.length > 0
            ? task.contractors.map(simpleTaskContractorLabel).join(', ')
            : renderEmptyProp()

    const renderTaskDescription = () => (!!task.description ? task.description : renderEmptyProp())

    const renderLocation = (location?: AosLocation) => {
        if (!location) {
            return
        }
        return (
            <Link onPress={() => previewLocation?.(task)}>
                {translate('event-details.select-location', { ns: 'common' })}
            </Link>
        )
    }

    const durationMinutes = !isTaskTemplate(task) ? (task as Task).spendTimeInMinutes : null

    const duration =
        durationMinutes !== undefined && durationMinutes !== null
            ? minutesToTaskDuration(durationMinutes)
            : undefined
    const durationField = duration && `${duration.hour}h ${duration.minute}min`

    const fromToText = !isTaskTemplate(task)
        ? (task as Task).startTime && (
              <Box>
                  <Box>{formatFromDateTime(translate)((task as Task).startTime)}</Box>
                  {(task as Task).endTime && (
                      <Box>{formatToDateTime(translate)((task as Task).endTime!)}</Box>
                  )}
              </Box>
          )
        : null

    const taskOnlyProps = ['tasks.task-preview.duration', 'tasks.task-preview.datetime']

    const isDark = rest.variant === ThemeVariant.Black
    const inkToken = isDark ? colors['aos-text'].default : colors['aos-text']['on-graphics']
    const inkTone = isDark ? 'default' : 'on-graphics'

    const renderSeverity = () => (
        <ChipSeverity
            className='self-start'
            tone={severityToneMap[task.severity]}
            label={translateTaskSeverity(translate)(task.severity)}
        />
    )

    const renderProcess = () => (
        <HStack className='items-center gap-2'>
            <Icon
                color={inkToken}
                svg={taskProcessTypeIcons[task.processType]}
                outline={task.processType === 'DAILY_TERMINAL'}
            />
            <UniversalText size='body' tone={inkTone}>
                {translateTaskProcessType(translate)(task.processType)}
            </UniversalText>
        </HStack>
    )
    const renderCategory = () => (
        <HStack className='items-center gap-2'>
            <TaskIcon category={task.category} color={inkToken} />
            <UniversalText size='body' tone={inkTone}>
                {translateTaskCategory(translate)(task.category)}
            </UniversalText>
        </HStack>
    )

    const items = dedupeKeepLast([
        ['tasks.task-preview.title', task.title ?? '-'],
        ['tasks.task-preview.description', renderTaskDescription()],
        ['tasks.task-preview.severity', renderSeverity()],
        ['tasks.task-preview.process-type', renderProcess()],
        ['tasks.task-preview.category', renderCategory()],
        ...DetailedTaskPropertyList(task, rest.variant),
        ['tasks.task-preview.billable', translateTaskBillable(translate)(task.billable)],
        ['tasks.task-preview.contractors', renderContractors],
        ['tasks.task-preview.assignees', renderAssignees],
        ['tasks.task-preview.location', renderLocation(task.location)],
        ['tasks.task-preview.duration', durationField],
        ['tasks.task-preview.datetime', fromToText],
        ...propsItems,
    ]).filter(item => (isTaskTemplate(task) ? !taskOnlyProps.includes(item[0]) : true))

    return (
        <>
            <DetailsList
                columns={rest.columns}
                tone={inkTone}
                items={items.map(([labelKey, value]) => ({
                    label: translateParts(translate)(labelKey),
                    // Values are either plain text or a rendered node (severity badge,
                    // assignee tags, …). Only the former goes through `optionalText`;
                    // casting a node to `string` would hand it to a formatter.
                    value:
                        value === undefined ||
                        value === null ||
                        typeof value === 'string' ||
                        typeof value === 'number'
                            ? optionalText(translate)(value ?? undefined)
                            : value,
                }))}
            />
            {renderAttachments(task.attachments)}
        </>
    )
}

export function dedupeKeepLast(items: readonly PropsListEntry[]) {
    const seen = new Set<string>()
    const out: PropsListEntry[] = []

    for (let i = items.length - 1; i >= 0; i--) {
        const [key, value] = items[i]
        if (seen.has(key)) {
            continue
        }
        seen.add(key)
        out.push([key, value])
    }

    out.reverse()
    return out
}
